import { useQuery } from '@tanstack/react-query';

import { Alert } from '../../../components/Alert';
import { Breadcrumbs } from '../../../components/Breadcrumbs';
import { PageHeader } from '../../../components/PageHeader';
import { PageSpinner } from '../../../components/PageSpinner';
import { StatusBadge } from '../../../components/StatusBadge';
import { systemHealthApi } from '../../../features/system-health/system-health.api';
import { toDisplayMessage } from '../../../lib/error-message';
import { paths } from '../../../routes/paths';
import styles from './AdminPortal.module.css';

function healthStatus(ok: boolean): string {
  return ok ? 'HEALTHY' : 'UNHEALTHY';
}

export function SystemHealthPage(): React.JSX.Element {
  const health = useQuery({
    queryKey: ['admin', 'system-health'],
    queryFn: () => systemHealthApi.get(),
  });

  return (
    <div className={styles.page}>
      <PageHeader
        title="System health"
        subtitle="Operational status of platform dependencies and background queues."
        breadcrumbs={
          <Breadcrumbs
            items={[{ label: 'Overview', to: paths.dashboard }, { label: 'System health' }]}
          />
        }
      />

      {health.isPending && <PageSpinner label="Loading system health" />}
      {health.isError && <Alert tone="error">{toDisplayMessage(health.error)}</Alert>}

      {health.isSuccess && (
        <>
          {health.data.mockProviderWarning && (
            <Alert tone="warning" title="Mock provider active">
              Payment processing is using the mock provider. This environment is not connected to
              live TransactPay.
            </Alert>
          )}

          <section className={styles.card} aria-labelledby="health-core-heading">
            <h2 className={styles.cardTitle} id="health-core-heading">
              Core services
            </h2>
            <dl className={styles.definitionList}>
              <dt>API</dt>
              <dd>
                <StatusBadge status={healthStatus(health.data.apiLive)} />
              </dd>
              <dt>Database</dt>
              <dd>
                <StatusBadge status={healthStatus(health.data.databaseReady)} />
              </dd>
              <dt>Payment provider</dt>
              <dd>{health.data.paymentProvider}</dd>
              <dt>Environment</dt>
              <dd>{health.data.environment}</dd>
              <dt>Application version</dt>
              <dd>{health.data.applicationVersion ?? '—'}</dd>
            </dl>
          </section>

          <section className={styles.card} aria-labelledby="health-queues-heading">
            <h2 className={styles.cardTitle} id="health-queues-heading">
              Background queues
            </h2>
            <dl className={styles.definitionList}>
              <dt>Notification backlog</dt>
              <dd>{health.data.notificationBacklog}</dd>
              <dt>Failed notifications</dt>
              <dd>{health.data.failedNotifications}</dd>
              <dt>Pending mandate polls</dt>
              <dd>{health.data.pendingMandatePolls}</dd>
              <dt>Pending payment reconciliation</dt>
              <dd>{health.data.pendingPaymentReconciliation}</dd>
            </dl>
          </section>
        </>
      )}
    </div>
  );
}
